'use client';

import { twMerge } from '@shared/themes';
import { useEffect, useState } from 'react';
import { JackpotByCharacter } from '../JackpotByCharacter/JackpotByCharacter';

type JackpotInfoProps = {
  variant: 'default' | 'sticky';
  label: string;
  refreshInterval: number;
  value: number;
  previousValue: number;
  icon?: React.ReactNode;
  highlight?: boolean;
  className?: string;
};

/**
 * Renders a particular jackpot title (e.g. rapid, daily, jumbo) and prize 'pill'.
 * Animates the value from the previous value to the new value over the refresh interval.
 */
function JackpotInfo({ variant, label, refreshInterval, value, previousValue, icon, highlight = false, className }: JackpotInfoProps) {
  const [animatedValue, setAnimatedValue] = useState<number>(previousValue);

  useEffect(() => {
    if (value === previousValue) return;
    const STEP_DURATION_MS = 50;
    const valueToAnimate = value - previousValue;
    const animationSteps = Math.max(1, Math.floor(refreshInterval / STEP_DURATION_MS));
    const stepSize = valueToAnimate / animationSteps;
    let newValue = previousValue;
    const interval = setInterval(() => {
      newValue += stepSize;
      if (newValue >= value) {
        setAnimatedValue(value);
        clearInterval(interval);
      } else {
        setAnimatedValue(Number(newValue.toFixed(2)));
      }
    }, STEP_DURATION_MS);
    return () => clearInterval(interval);
  }, [value, previousValue, refreshInterval]);

  const defaultStyles = {
    wrapper: 'flex flex-col items-center grow',
    label: 'mb-1',
    chip: twMerge('w-full flex justify-center rounded-full py-3 bg-background', highlight && 'border-2 border-accent-yellow'),
    chipContent: 'flex justify-center items-center gap-x-2 text-nowrap',
    value: 'flex items-center',
  };

  const variantStyles = {
    default: {
      ...defaultStyles,
      label: `${defaultStyles.label} paragraph-strong lg:paragraph-l-strong`,
      chip: `${defaultStyles.chip} px-6`,
      value: `${defaultStyles.value} ${highlight ? 'heading-4' : 'heading-5 lg:heading-4'}`,
    },
    sticky: {
      ...defaultStyles,
      label: `${defaultStyles.label} paragraph-l-strong`,
      value: `${defaultStyles.value} ${highlight ? 'heading-4 lg:heading-5' : 'heading-5'}`,
    },
  };

  const classes = variantStyles[variant];
  const characterWidth = variant === 'sticky' ? 'w-3.5 lg:w-3' : highlight ? 'w-3.5' : 'w-3 lg:w-3.5';

  return (
    <div className={twMerge(classes.wrapper, className)}>
      <p className={classes.label}>{label}</p>
      <div className={classes.chip}>
        <div className={classes.chipContent}>
          {icon ? <span>{icon}</span> : null}
          <JackpotByCharacter className={classes.value} value={animatedValue} characterWidth={characterWidth} />
        </div>
      </div>
    </div>
  );
}

export { JackpotInfo };
